/* eslint-disable @typescript-eslint/no-explicit-any */
import { Autocomplete, TextField } from '@mui/material';
import { FC, useState, useEffect } from 'react';
import { useForm } from '@carcareers/contexts';

export interface AutocompleteOption {
  label?: string;
  value?: any;
}

export interface AutocompleteInputProps {
  label: string;
  name: string;
  options: AutocompleteOption[];
  required?: boolean;
  fullWidth?: boolean;
  multiple?: boolean;
  value?: AutocompleteOption | AutocompleteOption[] | null;
}

export const AutocompleteInput: FC<AutocompleteInputProps> = ({
  label,
  name,
  options,
  required,
  fullWidth,
  multiple = false,
  value: propValue,
}) => {
  const { setData, getErrors } = useForm();
  const [value, setValue] = useState<AutocompleteOption | AutocompleteOption[] | null>(
    propValue ?? (multiple ? [] : null),
  );

  const truncateLabel = (label: string) => label.length > 42 ? `${label.substring(0, 42)}...` : label;

  const handleChange = (_: React.SyntheticEvent, newValue: AutocompleteOption | AutocompleteOption[] | null) => {
    setValue(newValue);

    if (multiple && Array.isArray(newValue)) {
      setData(
        name,
        newValue?.map((option) => option.value),
      );
    } else if (newValue) {
      setData(name, (newValue as AutocompleteOption).value);
    } else {
      setData(name, null);
    }
  };

  useEffect(() => {
    setValue(propValue ?? (multiple ? [] : null));
  }, [propValue, multiple]);

  return (
    <Autocomplete
      options={options?.map((option) => ({
        ...option,
        label: truncateLabel(option.label ?? ''),
      }))}
      value={value}
      multiple={multiple}
      fullWidth={fullWidth}
      onChange={handleChange}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={!!getErrors(name)}
          helperText={getErrors(name)}
        />
      )}
      isOptionEqualToValue={(option, value) => option.value === value.value}
    />
  );
};
